import React from "react";
import PropTypes from "prop-types";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
    CartesianGrid,
    LabelList,
    Tooltip,
} from "recharts";

/* ✅ K / L / Cr FORMATTER */
const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";

    if (value < 100000) {
        return `${(value / 1000).toFixed(1)} K`;
    }

    if (value < 10000000) {
        return `${(value / 100000).toFixed(1)} L`;
    }

    return `${(value / 10000000).toFixed(1)} Cr`;
};

/* ✅ TOOLTIP */
const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
        const data = payload?.[0]?.payload;

        return (
            <div
                style={{
                    background: "#fff",
                    padding: "12px 10px",
                    paddingTop: "5px",
                    borderRadius: "8px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
                    border: "1px solid #E6E8F0",
                    fontSize: "13px",
                }}
            >
                <div style={{ fontWeight: 600, marginBottom: "6px" }}>
                    {data.name}
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        color: "#0B5CAD",
                    }}
                >
                    <span>Loss Quantity &nbsp;&nbsp;</span>
                    <span>
                        {/* ✅ RAW VALUE ONLY */}
                        {data.value?.toLocaleString("en-IN")} {data.symbol}
                    </span>
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        color: "#9CA3AF",
                    }}
                >
                    <span>Percentage</span>
                    <span>{Number(data.percent).toFixed(1)}%</span>
                </div>
            </div>
        );
    }

    return null;
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            payload: PropTypes.shape({
                name: PropTypes.string,
                value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                symbol: PropTypes.string,
                percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            }),
        })
    ),
};

const ValidityBarLabel = ({ data, x, y, width, height, value, index }) => {
    const item = data?.[index];
    if (!item) return null;

    return (
        <text
            x={x + width + 10}
            y={y + height / 2}
            fill="#333"
            fontSize={12}
            dominantBaseline="middle"
        >
            {`${formatToCappedUnit(value)} ${item?.symbol} (${Number(
                item?.percent
            ).toFixed(1)}%)`}
        </text>
    );
};

ValidityBarLabel.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            symbol: PropTypes.string,
            percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
    ),
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    index: PropTypes.number,
};

const OrderToAllocationLossValidityGraph = ({ data }) => {
    const sortedData = [...data].sort((a, b) => a.order - b.order);
    const dynamicHeight = Math.max(sortedData.length * 50, 160);

    const yAxisWidth = 60;
    const rightMargin = 180;
    const maxValue = Math.max(...sortedData.map((d) => Number(d.value) || 0), 0);

    return (
        <div style={styles.graphContainer}>
            <div style={styles.scrollWrapper}>
                <div style={styles.chartInner}>
                    {/* ✅ MAIN CHART */}
                    <ResponsiveContainer width="100%" height={dynamicHeight}>
                        <BarChart
                            data={sortedData}
                            layout="vertical"
                            margin={{ top: 10, right: rightMargin, left: 0, bottom: 10 }}
                            barCategoryGap="20%"
                            barGap={2}
                        >
                            <CartesianGrid
                                stroke="#E6E8F0"
                                horizontal={false}
                                vertical={true}
                            />

                            {/* ✅ KEEP RAW (IMPORTANT FOR SCALING) */}
                            <XAxis
                                type="number"
                                domain={[0, maxValue]}
                                hide
                            />

                            <YAxis
                                type="category"
                                dataKey="name"
                                width={yAxisWidth}
                                tick={{ fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                            />

                            <Tooltip content={<CustomTooltip />} />

                            <Bar
                                dataKey="value"
                                fill="#0B5CAD"
                                barSize={16}
                                radius={[0, 10, 10, 0]}
                                minPointSize={5}
                            >
                                {/* ✅ LABEL FORMAT UPDATED */}
                                <LabelList
                                    data={sortedData}
                                    content={<ValidityBarLabel data={sortedData} />}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>

                    {/* ✅ STICKY BOTTOM AXIS (RAW VALUES) */}
                    <div style={styles.stickyBottomAxis}>
                        <ResponsiveContainer width="100%" height={35}>
                            <BarChart
                                layout="vertical"
                                data={[{ value: maxValue }]}
                                margin={{
                                    top: 0,
                                    right: rightMargin,
                                    left: yAxisWidth,
                                    bottom: 0,
                                }}
                            >
                                <XAxis
                                    type="number"
                                    domain={[0, maxValue]}
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

OrderToAllocationLossValidityGraph.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            order: PropTypes.number,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            symbol: PropTypes.string,
        })
    ),
};

const styles = {
    graphContainer: {
        background: "#fff",
        padding: "16px",
        paddingTop: "0px",
        borderRadius: "10px",
        width: "100%",
        overflow: "visible",
    },

    scrollWrapper: {
        width: "100%",
        height: "250px",
        overflowX: "auto",
        overflowY: "auto",
    },

    chartInner: {
        width: "100%",
        minHeight: "300px",
        position: "relative",
    },

    stickyBottomAxis: {
        position: "sticky",
        bottom: 0,
        background: "#fff",
        zIndex: 5,
    },
};

export default OrderToAllocationLossValidityGraph;